<template>
  <q-dialog v-model="dialogModel" persistent maximized>
    <q-card class="full-width">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6 text-brand-5 text-weight-bold">
          Monitoramento das Filas NATS
        </div>
        <q-space />
        <q-btn
          icon="refresh"
          flat
          round
          dense
          @click="buscarDados"
          :loading="loading"
          class="q-mr-sm"
        >
          <q-tooltip>Atualizar dados</q-tooltip>
        </q-btn>
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div v-if="loading" class="row justify-center q-pa-xl">
          <q-spinner-dots size="50px" color="primary" />
        </div>

        <div v-else-if="erro" class="row justify-center q-pa-xl">
          <div class="text-center">
            <q-icon name="error" size="50px" color="negative" class="q-mb-md" />
            <div class="text-h6 text-negative">Erro ao carregar dados</div>
            <div class="text-body2 text-grey-7 q-mt-sm">{{ erro }}</div>
            <q-btn
              label="Tentar novamente"
              color="primary"
              class="q-mt-md"
              @click="buscarDados"
            />
          </div>
        </div>

        <div
          v-else-if="consumersData.length === 0"
          class="row justify-center q-pa-xl"
        >
          <div class="text-center">
            <q-icon name="info" size="50px" color="info" class="q-mb-md" />
            <div class="text-h6">Nenhum consumer encontrado</div>
          </div>
        </div>

        <div v-else>
          <!-- Informações gerais do servidor -->
          <q-card flat bordered class="q-mb-md">
            <q-card-section>
              <div class="text-subtitle1 text-weight-bold text-brand-5 q-mb-sm">
                Informações do Servidor NATS
              </div>
              <div class="row q-col-gutter-md">
                <div class="col-md-2 col-sm-6 col-xs-12">
                  <div class="text-caption text-grey-7">Server ID</div>
                  <div
                    class="text-body2 text-weight-medium"
                    style="word-break: break-all; font-size: 0.8rem"
                  >
                    {{ serverInfo.server_id || "N/A" }}
                  </div>
                </div>
                <div class="col-md-2 col-sm-6 col-xs-12">
                  <div class="text-caption text-grey-7">Streams</div>
                  <div class="text-body2 text-weight-medium">
                    {{ serverInfo.streams || 0 }}
                  </div>
                </div>
                <div class="col-md-2 col-sm-6 col-xs-12">
                  <div class="text-caption text-grey-7">Consumers</div>
                  <div class="text-body2 text-weight-medium">
                    {{ serverInfo.consumers || 0 }}
                  </div>
                </div>
                <div class="col-md-2 col-sm-6 col-xs-12">
                  <div class="text-caption text-grey-7">Mensagens</div>
                  <div class="text-body2 text-weight-medium">
                    {{ serverInfo.messages || 0 }}
                  </div>
                </div>
                <div class="col-md-4 col-sm-12 col-xs-12">
                  <div class="text-caption text-grey-7">Última Atualização</div>
                  <div class="text-body2 text-weight-medium">
                    {{ formatDate(new Date()) }}
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Tabela de consumers -->
          <q-table
            :rows="consumersData"
            :columns="columns"
            row-key="name"
            flat
            bordered
            :rows-per-page-options="[10, 25, 50, 0]"
            :pagination="{ rowsPerPage: 25 }"
            class="consumers-table"
          >
            <template v-slot:body-cell-status="props">
              <q-td :props="props">
                <q-chip
                  :color="getStatusColor(props.row)"
                  text-color="white"
                  size="sm"
                  :label="getStatusLabel(props.row)"
                />
              </q-td>
            </template>

            <template v-slot:body-cell-last_active="props">
              <q-td :props="props">
                <div v-if="props.row.delivered?.last_active">
                  {{ formatDate(props.row.delivered.last_active) }}
                </div>
                <div v-else class="text-grey-5">Nunca ativo</div>
              </q-td>
            </template>

            <template v-slot:body-cell-actions="props">
              <q-td :props="props">
                <q-btn
                  flat
                  round
                  icon="visibility"
                  size="sm"
                  color="primary"
                  @click="verDetalhes(props.row)"
                >
                  <q-tooltip>Ver detalhes</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </q-table>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>

  <!-- Modal de detalhes do consumer -->
  <q-dialog v-model="showDetails" maximized>
    <q-card>
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6 text-brand-5 text-weight-bold">
          Detalhes do Consumer: {{ selectedConsumer?.name }}
        </div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>

      <q-card-section>
        <div v-if="selectedConsumer" class="q-pa-md">
          <div class="row q-col-gutter-md">
            <!-- Informações básicas -->
            <div class="col-md-6 col-sm-12">
              <q-card flat bordered>
                <q-card-section>
                  <div
                    class="text-subtitle1 text-weight-bold text-brand-5 q-mb-md"
                  >
                    Informações Básicas
                  </div>
                  <div class="row q-col-gutter-sm">
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Nome</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.name }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Stream</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.stream_name }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Criado em</div>
                      <div class="text-body2 text-weight-medium">
                        {{ formatDate(selectedConsumer.created) }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Cluster Leader</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.cluster?.leader || "N/A" }}
                      </div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- Estatísticas de entrega -->
            <div class="col-md-6 col-sm-12">
              <q-card flat bordered>
                <q-card-section>
                  <div
                    class="text-subtitle1 text-weight-bold text-brand-5 q-mb-md"
                  >
                    Estatísticas de Entrega
                  </div>
                  <div class="row q-col-gutter-sm">
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Consumer Seq</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.delivered?.consumer_seq || 0 }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Stream Seq</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.delivered?.stream_seq || 0 }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">
                        Última Atividade
                      </div>
                      <div class="text-body2 text-weight-medium">
                        {{
                          selectedConsumer.delivered?.last_active
                            ? formatDate(selectedConsumer.delivered.last_active)
                            : "Nunca ativo"
                        }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Pendentes</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.num_pending || 0 }}
                      </div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- ACK Floor -->
            <div class="col-md-6 col-sm-12">
              <q-card flat bordered>
                <q-card-section>
                  <div
                    class="text-subtitle1 text-weight-bold text-brand-5 q-mb-md"
                  >
                    ACK Floor
                  </div>
                  <div class="row q-col-gutter-sm">
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Consumer Seq</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.ack_floor?.consumer_seq || 0 }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Stream Seq</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.ack_floor?.stream_seq || 0 }}
                      </div>
                    </div>
                    <div class="col-12">
                      <div class="text-caption text-grey-7">
                        Última Atividade ACK
                      </div>
                      <div class="text-body2 text-weight-medium">
                        {{
                          selectedConsumer.ack_floor?.last_active
                            ? formatDate(selectedConsumer.ack_floor.last_active)
                            : "N/A"
                        }}
                      </div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- Estatísticas adicionais -->
            <div class="col-md-6 col-sm-12">
              <q-card flat bordered>
                <q-card-section>
                  <div
                    class="text-subtitle1 text-weight-bold text-brand-5 q-mb-md"
                  >
                    Estatísticas Adicionais
                  </div>
                  <div class="row q-col-gutter-sm">
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Processando</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.num_ack_pending || 0 }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Erros/DLQ</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.num_redelivered || 0 }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Aguardando</div>
                      <div class="text-body2 text-weight-medium">
                        {{ selectedConsumer.num_waiting || 0 }}
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="text-caption text-grey-7">Timestamp</div>
                      <div class="text-body2 text-weight-medium">
                        {{ formatDate(selectedConsumer.ts) }}
                      </div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>
      </q-card-section>
      <q-card-section>
        <div v-if="selectedConsumer" class="q-pa-md">
          <div class="row col-12 q-mt-lg">
            <div class="col-12">
              <q-card flat bordered>
                <q-card-section>
                  <div
                    class="text-subtitle1 text-weight-bold text-brand-5 q-mb-md"
                  >
                    Mensagens do Consumer
                  </div>
                  <q-table
                    :rows="dadosMensagens"
                    :columns="columnsMensagem"
                    row-key="streamSequence"
                    flat
                    bordered
                    color="primary"
                    class="consumers-table"
                    :rows-per-page-options="[50, 100]"
                    @request="paginarTabela"
                    :rows-per-page-label="'Linhas Por Página'"
                    v-model:pagination="pagination"
                  >
                  </q-table>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useQuasar } from "quasar";

export default {
  name: "ModalMonitoramentoNats",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const store = useStore();
    const $q = useQuasar();

    const dialogModel = computed({
      get: () => props.modelValue,
      set: (val) => emit("update:modelValue", val),
    });

    const loading = ref(false);
    const erro = ref(null);
    const dados = ref(null);
    const showDetails = ref(false);
    const selectedConsumer = ref(null);
    const dadosMensagens = ref([]);
    const pagination = ref({
      sortBy: "desc",
      page: 1,
      rowsPerPage: 50,
      rowsNumber: 6,
    });

    const serverInfo = computed(() => {
      if (!dados.value) return {};
      return {
        server_id: dados.value.server_id,
        streams: dados.value.streams,
        consumers: dados.value.consumers,
        messages: dados.value.messages,
      };
    });

    const consumersData = computed(() => {
      if (
        !dados.value?.account_details?.[0]?.stream_detail?.[0]?.consumer_detail
      ) {
        return [];
      }
      return dados.value.account_details[0].stream_detail[0].consumer_detail;
    });

    const columns = [
      {
        name: "name",
        label: "Nome do Consumer",
        field: "name",
        align: "left",
        sortable: true,
      },
      {
        name: "status",
        label: "Status",
        field: "status",
        align: "center",
        sortable: false,
      },
      {
        name: "consumer_seq",
        label: "Consumer Seq",
        field: (row) => row.delivered?.consumer_seq || 0,
        align: "center",
        sortable: true,
      },
      {
        name: "stream_seq",
        label: "Stream Seq",
        field: (row) => row.delivered?.stream_seq || 0,
        align: "center",
        sortable: true,
      },
      {
        name: "num_pending",
        label: "Pendentes",
        field: "num_pending",
        align: "center",
        sortable: true,
      },
      {
        name: "num_ack_pending",
        label: "Processando",
        field: "num_ack_pending",
        align: "center",
        sortable: true,
      },
      {
        name: "num_redelivered",
        label: "Erros/DLQ",
        field: "num_redelivered",
        align: "center",
        sortable: true,
      },
      {
        name: "last_active",
        label: "Última Atividade",
        field: "last_active",
        align: "center",
        sortable: false,
      },
      {
        name: "actions",
        label: "Ações",
        field: "actions",
        align: "center",
        sortable: false,
      },
    ];

    const columnsMensagem = [
      {
        name: "streamSequence",
        label: "ID",
        field: (row) => row.sequence,
        align: "left",
        sortable: true,
      },
      {
        name: "jobName",
        label: "Job",
        field: (row) => row.data?.displayName,
        align: "left",
      },
      {
        name: "timestamp",
        label: "Timestamp",
        field: (row) => row?.timestamp,
        align: "left",
      },
      {
        name: "websiteId",
        label: "Website ID",
        field: (row) => row.data?.website_id,
        align: "left",
      },
      {
        name: "tenant",
        label: "Tenant",
        field: (row) => row.data?.tenant,
        align: "left",
      },
    ];

    const buscarDados = async () => {
      loading.value = true;
      erro.value = null;

      try {
        const response = await store.dispatch("office/monitoramentoNats");
        dados.value = response.data;
      } catch (error) {
        console.error("Erro ao buscar dados do NATS:", error);
        erro.value =
          error.response?.data?.message || error.message || "Erro desconhecido";

        $q.notify({
          message: "Erro ao buscar dados do monitoramento NATS",
          color: "negative",
          actions: [
            {
              label: "Fechar",
              color: "white",
              handler: () => {},
            },
          ],
        });
      } finally {
        loading.value = false;
      }
    };

    const getStatusColor = (consumer) => {
      const hasActivity = consumer.delivered?.last_active;
      const hasPending = consumer.num_pending > 0;
      const hasAckPending = consumer.num_ack_pending > 0;

      if (!hasActivity) return "grey";
      if (hasPending || hasAckPending) return "orange";
      return "green";
    };

    const getStatusLabel = (consumer) => {
      const hasActivity = consumer.delivered?.last_active;
      const hasPending = consumer.num_pending > 0;
      const hasAckPending = consumer.num_ack_pending > 0;

      if (!hasActivity) return "Inativo";
      if (hasPending || hasAckPending) return "Com Pendências";
      return "Ativo";
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleString("pt-BR");
      } catch (error) {
        return "Data inválida";
      }
    };

    const verDetalhes = (consumer) => {
      selectedConsumer.value = consumer;
      showDetails.value = true;
    };

    const listarMensagensPorSubject = async (page = 1, limit = 50) => {
      let subjectName = selectedConsumer.value.name
        .split("_")
        .slice(1)
        .join("_");
      let streamName = selectedConsumer.value.stream_name;
      try {
        const response = await store.dispatch(
          "office/listarMensagensPorSubject",
          {
            subject: `${streamName}.${subjectName}`,
            page,
            limit,
          }
        );
        dadosMensagens.value = response.data.data;
        pagination.value.rowsPerPage = response.data.limit;
        pagination.value.page = response.data.pagination.currentPage;
        pagination.value.rowsNumber = response.data.pagination.totalMessages;
      } catch (error) {
        console.error("Erro ao buscar mensagens por subject:", error);
        return [];
      }
    };

    // Buscar dados quando o modal for aberto
    watch(dialogModel, (newVal) => {
      if (newVal) {
        buscarDados();
      }
    });

    watch(
      () => showDetails.value,
      () => {
        if (showDetails.value) {
          listarMensagensPorSubject();
        }
      }
    );

    const paginarTabela = (props) => {
      listarMensagensPorSubject(
        props.pagination.page,
        props.pagination.rowsPerPage
      );
    };

    return {
      dialogModel,
      loading,
      erro,
      dados,
      serverInfo,
      consumersData,
      columns,
      showDetails,
      selectedConsumer,
      buscarDados,
      getStatusColor,
      getStatusLabel,
      formatDate,
      verDetalhes,
      listarMensagensPorSubject,
      columnsMensagem,
      dadosMensagens,
      paginarTabela,
      pagination,
      pagesNumber: computed(() =>
        Math.ceil(pagination.value.rowsNumber / pagination.value.rowsPerPage)
      ),
    };
  },
};
</script>

<style scoped>
.consumers-table {
  .q-table__container {
    max-height: 70vh;
  }
}

.q-chip {
  font-weight: 500;
}
</style>
